import { Podcast, Star, Wallet } from 'lucide-react';
import { isNumber, range } from 'lodash';
import { useTranslation } from '@/lib/i18n';
import { cn, currencyFormatter, numberFormatter } from '@/lib/utils';
import { formatTimeAgo } from '@/lib/utils/date';
import { ChangeText } from '../cards/ChangeText';
import type { TrendingData, TrendCrypto, TrendEvent, TrendPortfolio } from '../ui/type';
import type { TrendingCardVariant } from './types';
import { formatCompactValue } from '@/lib/utils/number';
import { SocialEngagementCard } from '../cards/EventCard';
import { useTrackingWallets } from '@/contexts/TrackingWalletsProvider';
import { getMarketStatus } from '../utils/market';
import usePolyPnl from '../hooks/use-poly-pnl';
import usePolyPortfolio from '../hooks/use-poly-portfolio';

export function CardMeta({
  trend,
  kind,
  variant,
}: {
  trend: TrendingData;
  kind: string;
  variant: TrendingCardVariant;
}) {
  if (kind === 'event' && trend.event) {
    return <EventContent event={trend.event} variant={variant} />;
  }
  if (kind === 'market' && trend.market) {
    return <MarketContent market={trend.market} variant={variant} />;
  }
  if (kind === 'news' && trend.news) {
    return <NewsContent news={trend.news} variant={variant} />;
  }
  if (kind === 'crypto') {
    return <CryptoContent crypto={trend.crypto} variant={variant} />;
  }
  if (kind === 'portfolio') {
    return <PortfolioContent portfolio={trend.portfolio} variant={variant} />;
  }

  return <GeneralContent trend={trend} variant={variant} />;
}

export function CardBadge({ kind }: { kind: string }) {
  const { t } = useTranslation();

  return (
    <span className="self-start flex items-center justify-center rounded-sm border border-white/15 bg-black/60 px-1.5 h-5 text-xs uppercase leading-none text-white">
      {t(kind === 'question' ? 'general' : kind)}
    </span>
  );
}

function CryptoContent({
  crypto,
  variant,
}: {
  crypto?: TrendCrypto | null;
  variant: TrendingCardVariant;
}) {
  const { t } = useTranslation();
  const isImage = variant === 'image';
  const percentChange = crypto?.percent_change_24h ?? 0;
  const isPositive = percentChange >= 0;
  const sparklineColor = isPositive ? '#10D18A' : '#FF563B';

  return (
    <div
      className={cn(
        'mt-5',
        isImage ? 'flex flex-col gap-5' : 'pt-3.75 mt-auto flex items-end justify-between gap-3',
      )}
    >
      <div className="flex items-center gap-2">
        {crypto?.logo_url ? (
          <img
            src={crypto.logo_url}
            alt=""
            className="size-8 rounded-full bg-white/10 object-cover"
          />
        ) : (
          <span className="flex size-8 items-center justify-center rounded-full bg-[#F7931A] text-lg font-bold text-white">
            ₿
          </span>
        )}
        <div>
          <div className="font-semibold leading-5 text-white">
            {currencyFormatter(crypto?.price ?? 0)}
          </div>
          <div className="text-xs text-[#757575]">
            <span className={isPositive ? 'text-[#10D18A]' : 'text-[#FF563B]'}>
              {isPositive ? '+' : ''}
              {numberFormatter(percentChange, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
              %
            </span>{' '}
            ({t('24h')})
          </div>
        </div>
      </div>
      <div>
        <MiniSparkline color={sparklineColor} data={crypto?.kline} />
      </div>
    </div>
  );
}

function NewsContent({
  news,
  variant,
}: {
  news: TrendingData['news'];
  variant: TrendingCardVariant;
}) {
  if (variant === 'text') {
    return (
      <div className="pt-3.75 mt-auto">
        <TextSummary>{news?.title}</TextSummary>
        <ImpactInline importanceLevel={news?.importance_level} />
      </div>
    );
  }

  return (
    <div className="mt-6">
      <h4 className="line-clamp-3 text-sm leading-5 text-[#B7B9BC]">{news?.title}</h4>
      <ImpactInline variant={variant} importanceLevel={news?.importance_level} />
    </div>
  );
}

function MarketContent({
  market,
  variant,
}: {
  market: TrendingData['market'];
  variant: TrendingCardVariant;
}) {
  const eventTitle = market?.event?.title;
  const dispalyMarket = market?.markets?.[0];
  const { t } = useTranslation();
  const isImage = variant === 'image';
  const status = getMarketStatus(dispalyMarket);

  if (dispalyMarket) {
    if (variant === 'text') {
      return (
        <div className="pt-3.75 mt-auto">
          <TextSummary>{eventTitle || dispalyMarket.market_question}</TextSummary>
          <div className="mt-2 flex items-center gap-1 text-xs leading-none">
            {status && (
              <span
                className={cn(
                  'flex h-4 items-center rounded-sm bg-[#323131] px-1.75 text-[14px] leading-4 text-white capitalize',
                )}
              >
                {status}
              </span>
            )}
            <span className="font-semibold text-white">
              {(+dispalyMarket.outcome_yes_price * 100).toFixed(2)}%
            </span>
            <ChangeText value={+(dispalyMarket.price_change_24h || 0) * 100} />
            <span className="text-[#757575]">({t('24h')})</span>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-6">
        <h4
          className={cn(
            'leading-5 text-[#B7B9BC] text-xs',
            isImage ? 'line-clamp-3 text-sm' : 'line-clamp-1',
          )}
        >
          {eventTitle || dispalyMarket.market_question}
        </h4>
        <div
          className={cn(
            'flex items-center text-xs text-[#757575]',
            isImage ? 'flex-col items-start' : 'space-x-2',
          )}
        >
          <span className={cn('text-white', isImage ? 'text-base font-extrabold' : '')}>
            {(+dispalyMarket.outcome_yes_price * 100).toFixed(1)}%
          </span>
          <div className="space-x-1">
            <ChangeText value={+(dispalyMarket.price_change_24h || 0) * 100} />
            <span>({t('24h')})</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

function EventContent({
  event,
  variant,
}: {
  event: TrendingData['event'];
  variant: TrendingCardVariant;
}) {
  if (event) {
    if (variant === 'text') {
      return (
        <div className="pt-3.75 mt-auto">
          <TextSummary>{event.title}</TextSummary>
          <SocialInline socialSignal={event.social_signal} compact={false} />
        </div>
      );
    }

    return (
      <div className="flex flex-col mt-6">
        <h5 className="line-clamp-1 text-sm leading-6 text-[#B7B9BC] mb-2.5">{event.title}</h5>
        <SocialEngagementCard socialSignal={event.social_signal} />
      </div>
    );
  }
  return null;
}

function PortfolioContent({
  portfolio,
  variant,
}: {
  portfolio?: TrendPortfolio;
  variant: TrendingCardVariant;
}) {
  const { t } = useTranslation();
  // const currentValue = portfolio?.currentValue ?? portfolio?.totalValue ?? 0;

  const { allWallets } = useTrackingWallets();
  const { data, totalValue, isLoading } = usePolyPortfolio({
    wallets: allWallets,
  });
  const { data: chartHistory } = usePolyPnl({
    wallets: allWallets,
    interval: '1d',
    fidelity: '1h',
  });
  const firstValue = chartHistory.at(0)?.value;
  const lastValue = chartHistory.at(-1)?.value;
  const pnl = isNumber(firstValue) && isNumber(lastValue) ? lastValue - firstValue : 0;

  const pnlPercent = totalValue > 0 ? pnl / totalValue : 0;
  const percentChange = pnlPercent;
  const isPositive = percentChange > 0;
  const sparklineColor = isPositive ? '#10D18A' : '#FF563B';

  const sparklineData = chartHistory.map((item) => ({
    t: item.timestamp,
    p: item.value,
  }));

  if (portfolio) {
    return (
      <div
        className={
          variant === 'text'
            ? 'pt-3.75 mt-auto flex items-end justify-between gap-3'
            : 'mt-5 flex flex-col gap-5'
        }
      >
        <div className="whitespace-nowrap">
          <div className="flex items-center gap-3">
            {variant !== 'text' && (
              <div className="flex items-center justify-center shrink-0 size-10 rounded-full border border-white/15">
                <Wallet size={20} />
              </div>
            )}
            <div className="space-x-1 text-xs">
              <span className="text-lg font-semibold leading-7">
                {currencyFormatter(totalValue)}
              </span>
              <ChangeText value={pnlPercent} showTriangle={false} />
              <span className="text-[#757575]">({t('24h')})</span>
            </div>
          </div>
          <div className="space-x-2 leading-none">
            <span className="text-xs leading-none text-[#757575]">{t('Predictions')}:</span>
            <span className="text-sm leading-none">
              {numberFormatter(portfolio.marketsCount, { trailingZeroDisplay: 'stripIfInteger' })}
            </span>
          </div>
        </div>
        {chartHistory.length > 1 && <MiniSparkline color={sparklineColor} data={sparklineData} />}
      </div>
    );
  }
  return null;
}

function GeneralContent({ trend, variant }: { trend: TrendingData; variant: TrendingCardVariant }) {
  const { t } = useTranslation();
  return (
    <div
      className={cn('text-xs leading-5 text-[#757575]', variant === 'text' ? 'mt-3.75' : 'mt-6')}
    >
      <div>{t('Last Asked')}</div>
      <div className="text-[#B7B9BC]">{formatTimeAgo(trend.updated_at)}</div>
    </div>
  );
}

function SocialInline({
  socialSignal,
  compact,
}: {
  socialSignal?: TrendEvent['social_signal'];
  compact: boolean;
}) {
  const { t } = useTranslation();
  if (!socialSignal) return null;

  return (
    <div className={cn('flex items-center gap-2 uppercase text-white text-xs mt-1')}>
      <Podcast size={12} strokeWidth={2} />
      {/* <span>Social Engagement</span> */}
      <span>{t('Social')}</span>
      <span className={cn('font-semibold text-[#10D18A]', !compact && 'ml-auto text-sm')}>
        {formatCompactValue(socialSignal.total_interactions)}
      </span>
      <span className={cn('normal-case text-[#757575]', !compact && 'text-[10px]')}>
        {formatCompactValue(socialSignal.change_in_24h)}&nbsp;({t('24h')})
      </span>
    </div>
  );
}

function TextSummary({ children }: { children?: React.ReactNode }) {
  return <p className="line-clamp-1 text-xs text-[#B7B9BC]">{children}</p>;
}

function ImpactInline({
  importanceLevel = 0,
  variant = 'text',
}: {
  importanceLevel?: number;
  variant?: TrendingCardVariant;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'mt-2 flex gap-1 text-xs text-[#757575]',
        variant === 'text' ? 'items-center' : 'flex-col',
      )}
    >
      <span>{t('Impact')}: </span>
      <div className="flex items-center gap-1">
        {range(5).map((item) => (
          <Star
            key={`start_${item}`}
            stroke={item < importanceLevel ? '#FFBE49' : '#232323'}
            fill={item < importanceLevel ? '#FFBE49' : '#232323'}
            size={12}
          />
        ))}
      </div>
    </div>
  );
}

function MiniSparkline({
  color,
  data = [],
  width,
  height = 26,
}: {
  color: string;
  data?: Array<{ t: number; p: number }>;
  width?: number | string;
  height?: number;
}) {
  const viewBoxWidth = 66;
  const path = createSparklinePath(data, viewBoxWidth, height);
  const fallbackPath = createFallbackSparklinePath(viewBoxWidth, height);

  return (
    <svg
      className={cn('block', width === undefined && 'w-full')}
      width={width}
      height={height}
      viewBox={`0 0 ${viewBoxWidth} ${height}`}
      fill="none"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <path
        d={path || fallbackPath}
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function createSparklinePath(data: Array<{ t: number; p: number }>, svgWidth = 66, svgHeight = 26) {
  const points = data
    .map((point) => ({ t: Number(point.t), p: Number(point.p) }))
    .filter((point) => Number.isFinite(point.t) && Number.isFinite(point.p))
    .sort((a, b) => a.t - b.t);

  if (points.length < 2) return '';

  const offsetX = 1;
  const offsetY = 1;
  const width = Math.max(svgWidth - offsetX * 2, 1);
  const height = Math.max(svgHeight - offsetY * 2, 1);
  const minT = points[0].t;
  const maxT = points[points.length - 1].t;
  const prices = points.map((point) => point.p);
  const minP = Math.min(...prices);
  const maxP = Math.max(...prices);
  const timeRange = maxT - minT || 1;
  const priceRange = maxP - minP || Math.max(maxP * 0.01, 1);

  return points
    .map((point, index) => {
      const x = offsetX + ((point.t - minT) / timeRange) * width;
      const y = offsetY + height - ((point.p - minP) / priceRange) * height;
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
}

function createFallbackSparklinePath(svgWidth = 66, svgHeight = 26) {
  const points = [
    [1, 19.5],
    [14, 18.2],
    [24, 16.5],
    [34, 13.5],
    [43, 8.2],
    [52, 11.2],
    [65, 7.8],
  ];
  const scaleX = svgWidth / 66;
  const scaleY = svgHeight / 26;

  return points
    .map(
      ([x, y], index) =>
        `${index === 0 ? 'M' : 'L'}${(x * scaleX).toFixed(2)} ${(y * scaleY).toFixed(2)}`,
    )
    .join(' ');
}
